import React, { createContext, useContext, useMemo } from 'react'
import { z } from 'zod'

import {
  favoriteReportsResponseSchema,
  updateFavoriteReportResponseSchema,
} from 'pages/reports/helpers/reportFavorites'
import { RulePayload } from 'pages/rules/types/ruleConditions.types'

import { accountingMappingsSchema } from 'api/types/accountingMappingApiTypes'
import { fetchTokenContractAddressMappings } from 'api/types/imposterTokenAllowlistApiTypes'
import { fetchIngestionJobs } from 'api/types/ingestionJobsApiTypes'
import {
  priceOverrideUploadResponseSchema,
  priceOverrideUploadsResponseSchema,
} from 'api/types/priceOverrideTypes'
import {
  CreatePmaAnalysisRequest,
  fetchPmaAnalysesResponseSchema,
  fetchPmaCurrenciesResponseSchema,
  fetchPmaExchangesResponseSchema,
  pmaAnalysisSchema,
  runPmaAnalysisResponseSchema,
} from 'api/types/principalMarketAnalysisApiTypes'
import {
  VoucherAddCommentRequest,
  VoucherAddTransactionsRequest,
  VoucherCancelRequest,
  VoucherCreateRequest,
  VoucherDeleteRequest,
  VoucherEditDraftRequest,
  VoucherEditReviewRequest,
  VoucherEditSingleTransactionRequest,
  VoucherExportRequest,
  VoucherRemoveTransactionsRequest,
  VoucherReworkRequest,
  VoucherSubmitApprovalRequest,
  VoucherSubmitAuditRequest,
  VoucherSubmitCertificationRequest,
  VoucherSubmitDraftRequest,
  VoucherSyncRequest,
  voucherExportResponseSchema,
  voucherActionResponseSchema,
  voucherGetResponseSchema,
  voucherListResponseSchema,
  voucherUsersResponseSchema,
} from 'api/types/voucher/voucherApiSchemas'
import { OrgMetadata } from 'components/admin-navbar/org-select/OrgMetadataSelect'
import BookType from 'constants/BookType'
import DataSource from 'constants/sources/DataSource'
import { isZeusEnvironment } from 'utils/environment'

import getEndpointUrl from './getEndpointUrl'
import {
  FetchAnchorSwapsResponse,
  fetchAccountingConfigResponseSchema,
} from './types/accounting-settings/accountingSettingsApiTypes'
import { mappingsCsvResponseSchema } from './types/accountingMappingCsvApiTypeSchema'
import {
  FetchAccountingSyncStatusParams,
  FetchAccountingSyncStatusResponse,
} from './types/accountingSyncStatusesApiTypes'
import { FetchAllowedCategoriesResponse } from './types/categoryApiTypes'
import {
  ContactAddresses,
  FetchPaginatedUnknownAddressTransactionCountsParams,
} from './types/contact/contactApiTypes'
import {
  ContactRecentTransactions,
  addressTransactionCountsSchema,
  addressUnknownTransactionCountsSchema,
  contactAddressOptionsSchema,
  contactsSchema,
} from './types/contact/contactApiTypeSchemas'
import {
  FetchCsvIngestionsResponse,
  customErpCsvUploadResponseSchema,
  erpAccountsCsvByNameResponseSchema,
  erpAccountsCsvResponseSchema,
  regularCsvUploadResponseSchema,
} from './types/csvIngestionTypes'
import {
  CreateCustomFieldRequest,
  CustomField,
  CustomFieldEditRequest,
  deleteCustomFieldResponseSchema,
  getCustomFieldsResponseSchema,
  postCustomFieldResponseSchema,
  putCustomFieldResponseSchema,
} from './types/customFieldsApiTypeSchema'
import {
  DashboardFilterParamType,
  DashboardParamsType,
  FetchAssetByDateResponse,
  FetchCurrentAssetsApiResponse,
  FetchDashboardSummaryResponse,
  FetchMainTransactionCountResponse,
} from './types/dashboardApiTypes'
import { dateRangeSchema } from './types/dateRangeTypes'
import {
  erpAccountSchema,
  erpClassSchema,
  erpCustomerSchema,
  erpLocationSchema,
  erpVendorSchema,
} from './types/erpEntityTypes'
import { fetchErpStatusResponseSchema } from './types/erpStatusApiSchema'
import { erpSyncPreviewResponseSchema } from './types/erpSyncPreviewType'
import { FetchExchangeRatesResponse } from './types/exchangeRatesApiTypes'
import { FetchFilterOptionsParams, FetchFilterOptionsResponse } from './types/filterApiTypes'
import {
  AirflowDagContext,
  airflowDagNextRunSchema,
  airflowDagRunSchema,
  airflowDagRunsResponseSchema,
  airflowTaskInstanceSchema,
} from './types/gcDagApiTypes'
import { FetchUserOrgEntitiesResponse } from './types/legalEntityApiTypes'
import { CreateOrgPayload, orgsByParentResponseSchema } from './types/orgsApiTypes'
import { pendingChangesResponseSchema } from './types/pendingChangesApiTypes'
import {
  FetchStableCoinsResponse,
  RateOverride,
  RateOverrideParamsType,
} from './types/rateOverridesApiTypes'
import {
  FetchPaginatedReportRequestParams,
  FetchPaginatedReportResponse,
  FetchReportDownloadUrlParams,
  FetchReportDownloadUrlResponse,
  NonSpecificReportType,
  ReportType,
} from './types/report/reportApiTypes'
import {
  asc35060ReportSchema,
  assetReportSchema,
  balanceSchema,
  fairMarketValueLotDispositionsSchema,
  fairMarketValueLotEndingInventorySchema,
  fairMarketValueLotLevelSchema,
  fairMarketValueSummarySchema,
  gainLossReportSchema,
  gainlossSummarySchema,
  transactionEditHistorySchema,
  impairedAssetsSchema,
  impairmentDispositionSchema,
  qaImpairmentSummarySchema,
  impairmentLotEndingInventorySchema,
  impairmentLotLevelReportSchema,
  impairmentSummaryReportSchema,
  journalEntryDetailedSchema,
  journalEntrySummarySchema,
  legacyRollForwardSchema,
  nftSchema,
  pricesDailySummarySchema,
  qaAssetDataSchema,
  qaBalanceSchema,
  qaFairMarketValueSummarySchema,
  qaGlobalAssetHoldingsSchema,
  qaGlobalEntityHoldingsSchema,
  qaJournalEntryDetailedSchema,
  qaRollForwardSchema,
  qaSignificantHoldingsSchema,
  reconciliationRollForwardReportSchema,
  rollingGainLossReportSchema,
  significantHoldingsDetailedSchema,
  significantHoldingsReportSchema,
  updateImpairedAssetsSchema,
} from './types/report/reportApiTypeSchemas'
import { FetchRulesResponse } from './types/rulesApiTypes'
import {
  PricingMarketProvider,
  UpdatePricingMethodRequest,
  UpdatePricingSettingRequest,
  deletePricingOverrideResponseSchema,
  fetchOrgMetadataResponseSchema,
  fetchOrgSettingsResponseSchema,
  fetchPricingOverridesResponseSchema,
  fetchPricingSettingsResponseSchema,
  upsertPricingOverrideResponseSchema,
  RefreshPricesRequest,
  refreshPricesResponseSchema,
} from './types/settingsApiTypes'
import { FetchSourceStatusesResponse } from './types/sourceApiTypes'
import { sourceTagsResponseSchema } from './types/sourceTagsApiTypes'
import { FetchSpamTokensResponse, SpamTokenChain } from './types/spamTokenApiTypes'
import { Status } from './types/statusApiTypes'
import { FetchMergeLinkTokenResponse } from './types/tokenApiTypes'
import {
  BulkEditStatusParams,
  FetchEarliestTransactionDateResponse,
  FetchPaginatedTransactionsResponse,
  TransactionAnnotationUpdates,
  TransactionEdit,
  TransactionFilterParams,
} from './types/transaction/transactionApiTypes'
import {
  bulkEditStatusSchema,
  categoryMetadataSchema,
  transactionCountSchema,
  transactionExportSchema,
  transactionTypeSchema,
} from './types/transaction/transactionApiTypeSchemas'
import {
  CreateTransactionCategoryParams,
  FetchTransactionCategoryParams,
} from './types/transaction-category/transactionCategoryApiTypes'
import { fetchTransactionCategoriesResultSchema } from './types/transaction-category/transactionCategoryApiTypeSchemas'
import { User, userSchema } from './types/userApiTypes'
import {
  BulkCreateWalletsRequest,
  BulkCreateWalletsResponse,
  UpsertWalletRequest,
  Wallet,
} from './types/walletApiTypes'
import useFetchWithAuth from './utils/useFetchWithAuth'

type IaasIngestionResponse = {
  result: { result: string }
}

function useApiInternal() {
  const fetchWithAuth = useFetchWithAuth()

  const api = useMemo(() => {
    return {
      fetch_org: () => fetchWithAuth<{ org: unknown }>('org').then((resp) => resp.org),

      create_org: (orgData: CreateOrgPayload) =>
        fetchWithAuth('org', {
          method: 'POST',
          data: orgData,
        }),

      fetch_all_orgs_by_parent: () =>
        fetchWithAuth('orgs_by_parent', {
          responseSchema: orgsByParentResponseSchema,
        }).then((resp) => resp.result),

      fetch_export_org: () => fetchWithAuth('org/export'),

      import_org: (data: unknown) => fetchWithAuth('org', { method: 'POST', data }),

      delete_org: (orgToDelete: string) =>
        fetchWithAuth('org', { method: 'DELETE', data: { org: orgToDelete } }),

      update_org_display_name: (display_name: string) =>
        fetchWithAuth('settings/overview', {
          method: 'POST',
          data: { display_name },
        }),

      fetch_wallets: () =>
        fetchWithAuth<{ wallets: Wallet[] }>('wallets').then((resp) =>
          resp.wallets.filter((w: Wallet) => w.type !== 'External')
        ),

      upsert_wallet: (wallet: UpsertWalletRequest) =>
        fetchWithAuth('wallets', {
          method: 'POST',
          data: wallet,
        }),

      delete_wallet: (wallet: UpsertWalletRequest) =>
        fetchWithAuth('wallets', {
          method: 'DELETE',
          data: wallet,
        }),

      bulk_create_wallets: (data: BulkCreateWalletsRequest) =>
        fetchWithAuth<BulkCreateWalletsResponse>('wallets/bulk', {
          method: 'POST',
          data,
        }),

      fetch_wallet_date_ranges: (wallet_id: string) =>
        fetchWithAuth(`wallets/${wallet_id}/date_range`, {
          responseSchema: z.object({ result: z.array(dateRangeSchema) }),
        }),

      create_wallet_date_range: (
        wallet_id: string,
        start_date: string | null,
        end_date: string | null
      ) =>
        fetchWithAuth(`wallets/${wallet_id}/date_range`, {
          method: 'POST',
          data: { start_date, end_date },
          responseSchema: z.object({ result: dateRangeSchema }),
        }),

      delete_wallet_date_range: (wallet_id: string, id: string) =>
        fetchWithAuth(`wallets/${wallet_id}/date_range/${id}`, {
          method: 'DELETE',
        }),

      update_wallet_date_range: (
        wallet_id: string,
        id: string,
        start_date: string | null,
        end_date: string | null
      ) =>
        fetchWithAuth(`wallets/${wallet_id}/date_range/${id}`, {
          method: 'PUT',
          data: { start_date, end_date },
          responseSchema: z.object({ result: dateRangeSchema }),
        }),

      fetch_users: () => fetchWithAuth<{ users: User[] }>('user'),

      fetch_current_user: () => fetchWithAuth('user/current_user', { responseSchema: userSchema }),

      upsert_user: (user: Pick<User, 'user_name' | 'email' | 'role'>) =>
        fetchWithAuth('user', {
          method: 'POST',
          data: user,
        }),

      delete_user: (user: Pick<User, 'user_name' | 'email' | 'role'>) =>
        fetchWithAuth('user', {
          method: 'DELETE',
          data: user,
        }),

      sync_user_metadata: () =>
        fetchWithAuth('sync_user_metadata', {
          method: 'POST',
        }),

      // e.g. using the transaction/paginate endpoint
      // fetch_paginated_transactions: () => {
      //   return fetchWithAuth('transaction/paginate', {
      //     data: {
      //       next_token:
      //         'AS/es+kZXpY+fmj/pIZ1enJ0QldV/BF85VtlBiMlOJ/f9A2fgIERdEeB48lbFBmP7LSAmKFN/sRVWocnse1tPTpTH2GVee3gBQ==',
      //       query_execution_id: '4b667d43-25fa-41d4-a04c-84e4f37c01e1',
      //     },
      //   })
      // },
      // e.g. filtering
      // fetch_paginated_transactions: () => {
      //   return fetchWithAuth('transaction/paginate', {
      //     data: {
      //       start: "2023-06-01",
      //       end: "2023-06-10",
      //       // asset: ["ETH"],
      //       // category: ["Internal"],
      //       asset_type: ["NFT"], // ["Cryptocurrency"] or ["NFT", "Cryptocurrency"]
      //     },
      //   })
      // },
      fetch_paginated_transactions: (params: unknown) => {
        return fetchWithAuth<FetchPaginatedTransactionsResponse>('transaction/paginate', {
          method: 'POST',
          data: params,
        })
      },

      save_transaction_edits: (
        txnIds: TransactionEdit[],
        annotationUpdates: TransactionAnnotationUpdates
      ) =>
        fetchWithAuth('transactions/edits', {
          method: 'POST',
          data: { txnIds, annotationUpdates },
        }),

      save_select_all_transaction_edits: (
        annotationUpdates: TransactionAnnotationUpdates,
        filters: TransactionFilterParams
      ) =>
        fetchWithAuth('transactions/select_all_edits', {
          method: 'POST',
          data: { annotationUpdates, filters },
        }),

      upload_bulk_edit_csv: (formdata: FormData) =>
        fetchWithAuth('transactions/csv_bulk_edit_wide', {
          method: 'POST',
          body: formdata,
        }),

      fetch_accounting_sync_statuses_with_filters: (params: TransactionFilterParams) =>
        fetchWithAuth<FetchAccountingSyncStatusResponse>(
          'transactions/accounting_sync_statuses/filters',
          {
            params,
          }
        ),

      create_or_update_rule: (
        name: string,
        ifStatement: string,
        thenStatement: string,
        shouldApplyRuleToPrevTxns: boolean,
        id: string
      ) =>
        fetchWithAuth('rule', {
          method: 'POST',
          data: {
            name,
            id,
            if_statement: ifStatement,
            then_statement: thenStatement,
            apply_rule_to_prev_txns: shouldApplyRuleToPrevTxns,
          },
        }),

      create_or_update_rule_new: (payload: RulePayload) =>
        fetchWithAuth('rule', {
          method: 'POST',
          data: payload,
        }),

      delete_rules: (ruleIds: string[]) =>
        fetchWithAuth('rule', {
          method: 'DELETE',
          data: { rule_ids: ruleIds },
        }),

      update_rule_priority: (ruleIds: string[]) =>
        fetchWithAuth('rule/priority', {
          method: 'POST',
          data: { rule_ids: ruleIds },
        }),

      fetch_rules: () => fetchWithAuth<FetchRulesResponse>('rule').then((resp) => resp.rules),

      fetch_custom_fields: () =>
        fetchWithAuth('custom_fields', {
          responseSchema: getCustomFieldsResponseSchema,
        }).then((resp) => resp.custom_fields),

      create_custom_field: (customField: CreateCustomFieldRequest) =>
        fetchWithAuth('custom_fields', {
          method: 'POST',
          responseSchema: postCustomFieldResponseSchema,
          data: customField,
        }),

      update_custom_field: (id: CustomField['id'], customField: CustomFieldEditRequest) =>
        fetchWithAuth(`custom_fields/${id}`, {
          method: 'PATCH',
          data: customField,
          responseSchema: putCustomFieldResponseSchema,
        }).then((resp) => resp.custom_field),

      delete_custom_field: (id: CustomField['id']) =>
        fetchWithAuth(`custom_fields/${id}`, {
          method: 'DELETE',
          responseSchema: deleteCustomFieldResponseSchema,
        }).then((resp) => resp.custom_field),

      fetch_contacts: () =>
        fetchWithAuth('contacts', {
          responseSchema: contactsSchema,
        }),

      upsert_contact: (contact: unknown) =>
        fetchWithAuth('contacts', {
          method: 'POST',
          data: contact,
        }),

      delete_contact: (contact: { name: string; addr: string }) =>
        fetchWithAuth('contacts', {
          method: 'DELETE',
          data: contact,
        }),

      fetch_contact_address_options: () =>
        fetchWithAuth('contacts/address_options', {
          responseSchema: contactAddressOptionsSchema,
        }),

      fetch_contact_recent_transactions: (contactAddresses: ContactAddresses) =>
        fetchWithAuth<ContactRecentTransactions>('contacts/recent_transactions', {
          method: 'POST',
          data: { contactAddresses },
        }),

      fetch_contact_paginated_unknown_address_transaction_counts: (
        params: FetchPaginatedUnknownAddressTransactionCountsParams
      ) =>
        fetchWithAuth('contacts/unknown_address_transaction_counts', {
          params,
          responseSchema: addressUnknownTransactionCountsSchema,
        }),

      fetch_contact_transaction_counts: () =>
        fetchWithAuth('contacts/contact_transaction_counts', {
          responseSchema: addressTransactionCountsSchema,
        }),

      fetch_categories_allowed: () =>
        fetchWithAuth<FetchAllowedCategoriesResponse>('transaction_categories'),

      fetch_category_metadata: () =>
        fetchWithAuth('transaction_category_metadata', {
          responseSchema: categoryMetadataSchema,
        }),

      fetch_transaction_types: () =>
        fetchWithAuth('transaction_types', {
          responseSchema: transactionTypeSchema,
        }),

      fetch_token_contract_address_mappings: () =>
        fetchWithAuth('imposter_token_allowlist', {
          responseSchema: fetchTokenContractAddressMappings,
        }),

      add_token_contract_address_mappings: (data: {
        tokenSymbol: string
        tokenId: string
        transactionSource: string
        contractAddress: string
      }) =>
        fetchWithAuth('imposter_token_allowlist', {
          method: 'POST',
          data,
        }),

      fetch_ingestion_jobs: () =>
        fetchWithAuth('ingestion_jobs', { responseSchema: fetchIngestionJobs }),

      fetch_spam_tokens: () => fetchWithAuth<FetchSpamTokensResponse>('spam_tokens'),

      trigger_auto_spam_ingestion: () =>
        fetchWithAuth('trigger_auto_spam_ingestion', { method: 'POST' }),

      add_spam_token: (chain: SpamTokenChain, address: string) =>
        fetchWithAuth('spam_tokens', {
          method: 'POST',
          data: { chain, address },
        }),

      delete_spam_token: (chain: SpamTokenChain, address: string) =>
        fetchWithAuth<string>('spam_tokens', {
          method: 'DELETE',
          data: { chain, address },
        }),

      fetch_coinbase_iaas_authz_url: () => fetchWithAuth('coinbase_iaas/oauth/authorization_url'),

      fetch_exchange_rates: () => fetchWithAuth<FetchExchangeRatesResponse>('exchange_rates'),

      fetch_erp_status: () =>
        fetchWithAuth('accounting/status', {
          responseSchema: fetchErpStatusResponseSchema,
        }),

      fetch_erp_accounts: () =>
        fetchWithAuth('accounting/account', {
          responseSchema: z.record(erpAccountSchema),
        }),

      fetch_erp_customers: () =>
        fetchWithAuth('accounting/customers', {
          responseSchema: z.record(erpCustomerSchema),
        }),

      fetch_erp_vendors: () =>
        fetchWithAuth('accounting/vendors', {
          responseSchema: z.record(erpVendorSchema),
        }),

      fetch_erp_classes: () =>
        fetchWithAuth('accounting/classes', {
          responseSchema: z.record(erpClassSchema),
        }),

      fetch_erp_locations: () =>
        fetchWithAuth('accounting/locations', {
          responseSchema: z.record(erpLocationSchema),
        }),

      fetch_accounting_mappings: () =>
        fetchWithAuth('accounting/mapping', {
          responseSchema: accountingMappingsSchema,
        }),

      fetch_accounting_mappings_csv: () =>
        fetchWithAuth('accounting/mappings/csv', {
          responseSchema: mappingsCsvResponseSchema,
        }),

      disconnect_accounting_erp: () => fetchWithAuth('accounting/erp/revoke'),

      fetch_anchor_swaps: () => fetchWithAuth<FetchAnchorSwapsResponse>('transaction/anchor_swaps'),

      fetch_internals_migration_date: () => fetchWithAuth('transaction/internals_migration_date'),

      fetch_consider_historic_internals_for_sync_date: (sync_date_string: string) =>
        fetchWithAuth<{ result: unknown }>(
          `transaction/consider_historic_internals/${sync_date_string}`
        ).then((resp) => resp.result),

      fetch_sync_doc_number_prefix: () =>
        fetchWithAuth<{ result: unknown }>('transaction/sync_doc_number_prefix'),

      fetch_rate_overrides: () => fetchWithAuth('accounting/rate_overrides'),

      fetch_stable_coins: () => fetchWithAuth<FetchStableCoinsResponse>('accounting/stable_coins'),

      fetch_attachments_for_txn: (txnId: string) =>
        fetchWithAuth<{ result: unknown }>(`transaction/${txnId}/attachment`).then(
          (resp) => resp.result
        ),

      delete_attachments_for_txn: (txnId: string, attachmentHashes: string[]) =>
        fetchWithAuth(`transaction/${txnId}/attachment`, {
          method: 'DELETE',
          responseSchema: z.object({
            result: z.record(
              z.string().describe('Attachment file hash'),
              z.boolean().describe('Whether the attachment was successfully deleted')
            ),
          }),
          data: attachmentHashes,
        }),

      upload_attachments_for_txn: (txnId: string, formdata: FormData) =>
        fetchWithAuth(`transaction/${txnId}/attachment`, {
          method: 'POST',
          body: formdata,
        }),

      update_anchor_swaps: (setting: boolean) =>
        fetchWithAuth<FetchAnchorSwapsResponse>('transaction/anchor_swaps', {
          method: 'POST',
          data: { org_setting: 'anchor_swaps', value: setting },
        }),

      update_break_up_internals: (setting: string) =>
        fetchWithAuth('transaction/internals_migration_date', {
          method: 'POST',
          data: { org_setting: 'break_up_internals_migration_date', value: setting },
        }),

      update_sync_doc_number_prefix: (setting: string) =>
        fetchWithAuth('transaction/sync_doc_number_prefix', {
          method: 'POST',
          data: { org_setting: 'sync_doc_number_prefix', value: setting },
          responseSchema: z.object({ result: z.string() }),
        }),

      update_rate_overrides: (overrides: RateOverrideParamsType[]) =>
        fetchWithAuth<{ result: RateOverride[] }>('accounting/rate_overrides', {
          method: 'POST',
          data: { overrides },
        }),

      delete_rate_overrides: (ids: string[]) =>
        fetchWithAuth<string>('accounting/rate_overrides', {
          method: 'DELETE',
          data: { ids },
        }),

      fetch_accounting_mapping: (mappingType: string, mappingQualifier: string) =>
        mappingQualifier
          ? fetchWithAuth(`accounting/mapping/${mappingType}/${mappingQualifier}`)
          : fetchWithAuth(`accounting/mapping/${mappingType}`),

      upsert_accounting_mapping: (
        mapping: unknown,
        mappingType: string,
        mappingQualifier: string | null,
        mappingQualifiers?: string[] | null
      ) =>
        fetchWithAuth('accounting/mapping', {
          method: 'POST',
          data: {
            mapping,
            mappingType,
            mappingQualifier,
            ...(mappingQualifiers !== undefined && mappingQualifiers !== null
              ? { mappingQualifiers }
              : {}),
          },
        }),

      delete_accounting_detailed_mappings: (
        mappingType: string,
        mappingQualifier: string,
        mappingQualifiers?: string[] | null
      ) =>
        fetchWithAuth('accounting/detailed_mapping', {
          method: 'DELETE',
          data: {
            mappingType,
            mappingQualifier,
            ...(mappingQualifiers !== undefined && mappingQualifiers !== null
              ? { mappingQualifiers }
              : {}),
          },
        }),

      post_erp_syncs: (
        data:
          | {
              transaction_group_ids_and_dates: Array<{
                transaction_group_id: string
                transaction_date: string
              }>
              is_aggregate: boolean
              is_resync: boolean
            }
          | {
              filters: TransactionFilterParams
              is_aggregate: boolean
              is_resync: boolean
            }
      ) =>
        fetchWithAuth('accounting/erp_sync', {
          method: 'POST',
          data,
        }),

      get_erp_syncs_preview: (
        data:
          | {
              transaction_group_ids_and_dates: Array<{
                transaction_group_id: string
                transaction_date: string
              }>
              is_aggregate: boolean
            }
          | {
              filters: TransactionFilterParams
              is_aggregate: boolean
              is_resync: boolean
            }
      ) =>
        fetchWithAuth('accounting/erp_sync/preview', {
          method: 'POST',
          data,
          responseSchema: erpSyncPreviewResponseSchema,
        }),

      patch_erp_syncs: (
        transactionGroupIdAndDatesByJournalId: Record<string, Array<Record<string, string>>>
      ) =>
        fetchWithAuth('accounting/erp_sync', {
          method: 'PATCH',
          data: {
            transaction_group_ids_and_dates_by_journal_ids: transactionGroupIdAndDatesByJournalId,
          },
        }),

      fetch_merge_link_token: (erp_name = '') =>
        fetchWithAuth<FetchMergeLinkTokenResponse>('accounting/create_link_token', {
          method: 'POST',
          data: { erp_name },
        }),

      upload_merge_public_token: (erp_name: string, public_token: string) =>
        fetchWithAuth('accounting/retrieve_account_token', {
          method: 'POST',
          data: { erp_name, public_token },
        }),

      fetch_current_erp_entries: () => fetchWithAuth('accounting/journalentry'),

      delete_journal_entries_by_id: (journalIds: string[]) =>
        fetchWithAuth('accounting/journalentries_by_id', {
          method: 'DELETE',
          data: { journal_ids: journalIds },
        }),

      ingest_csv: (formdata: FormData) =>
        fetchWithAuth('ingestion/csv', {
          method: 'POST',
          responseSchema: regularCsvUploadResponseSchema,
          body: formdata,
        }),

      fetch_csv_ingestions: () => fetchWithAuth<FetchCsvIngestionsResponse>('ingestion/csv'),

      fetch_imported_csv: (name: string) =>
        fetchWithAuth<{ csv: string }>('ingestion/csv', {
          method: 'GET',
          params: { name },
        }),

      delete_csv_ingestion: (name: string) =>
        fetchWithAuth('ingestion/csv', {
          method: 'DELETE',
          data: { name },
        }),

      check_invalid_raw_csv_transactions: () =>
        fetchWithAuth<{ exists: boolean }>('ingestion/invalid_raw_csv_transactions'),

      export_invalid_raw_csv_transactions: () =>
        fetchWithAuth<{ url: string }>('ingestion/invalid_raw_csv_transactions/export'),

      fetch_status: () =>
        fetch(getEndpointUrl('status/report', false)).then((resp) =>
          resp.json()
        ) as Promise<Status>,

      fetch_taxbit_integration_date: (integration_name: string) =>
        fetchWithAuth(`taxbit_internal_transactions/integration/date/${integration_name}`),

      validate_taxbit_integration_access: (integration_name: string, credentials: unknown) =>
        fetchWithAuth<{ validation: unknown }>(
          `taxbit_internal_transactions/api_key/validate/${integration_name}`,
          {
            method: 'POST',
            data: { credentials },
          }
        ).then((resp) => resp.validation),

      save_taxbit_integration_credentials: (integration_name: string, credentials: unknown) =>
        fetchWithAuth(`taxbit_internal_transactions/api_key/save/${integration_name}`, {
          method: 'POST',
          data: { credentials },
        }),

      request_taxbit_integration_transactions: (integration_name: string, address = '') =>
        fetchWithAuth<IaasIngestionResponse>(
          `taxbit_internal_transactions/ingestion_request/${integration_name}`,
          {
            params: address ? { address } : {},
          }
        ),

      retrieve_taxbit_integration_transactions: (integration_name: string, address = '') =>
        fetchWithAuth<IaasIngestionResponse>(
          `taxbit_internal_transactions/retrieve_ingestion/${integration_name}`,
          {
            params: address ? { address } : {},
          }
        ),

      delete_request_taxbit_integration_transactions: (integration_name: string, address = '') =>
        fetchWithAuth<IaasIngestionResponse>(
          `taxbit_internal_transactions/ingestion_request/${integration_name}`,
          {
            method: 'DELETE',
            data: address ? { address } : {},
          }
        ),

      fetch_taxbit_integration_status: (integration_name: string, address = '') =>
        fetchWithAuth(`taxbit_internal_transactions/status/${integration_name}`, {
          params: address ? { address } : {},
        }),

      disconnect_taxbit_integration: (integration_name: string) =>
        fetchWithAuth(`taxbit_internal_transactions/api_key/revoke/${integration_name}`),

      fetch_integration_date_ranges: (integration_name: string) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/date_range`, {
          responseSchema: z.object({ result: z.array(dateRangeSchema) }),
        }),

      create_integration_date_range: (
        integration_name: string,
        start_date: string | null,
        end_date: string | null
      ) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/date_range`, {
          method: 'POST',
          data: { start_date, end_date },
          responseSchema: z.object({ result: dateRangeSchema }),
        }),

      delete_integration_date_range: (integration_name: string, id: string) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/date_range/${id}`, {
          method: 'DELETE',
        }),

      update_integration_date_range: (
        integration_name: string,
        id: string,
        start_date: string | null,
        end_date: string | null
      ) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/date_range/${id}`, {
          method: 'PUT',
          data: { start_date, end_date },
          responseSchema: z.object({ result: dateRangeSchema }),
        }),

      fetch_exchange_source_tags: (integration_name: string) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/source_tags`, {
          responseSchema: sourceTagsResponseSchema,
        }).then((resp) => resp.result),

      update_exchange_source_tags: (integration_name: string, source_tags: string[]) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/source_tags`, {
          method: 'POST',
          data: { source_tags },
        }),

      delete_exchange_source_tags: (integration_name: string, tags: string[]) =>
        fetchWithAuth(`taxbit_internal_transactions/${integration_name}/source_tags`, {
          method: 'DELETE',
          data: { tags },
        }),

      fetch_all_source_tags: () =>
        fetchWithAuth('source_tags', {
          responseSchema: sourceTagsResponseSchema,
        }).then((resp) => resp.result),

      fetch_sources: () =>
        fetchWithAuth<{ config: { sources: DataSource[] } }>('config').then(
          (data) => data.config.sources
        ),

      fetch_source_statuses: () => fetchWithAuth<FetchSourceStatusesResponse>('source/status'),

      fetch_default_sources: () =>
        fetchWithAuth<{ sources: DataSource[] }>(
          isZeusEnvironment() ? 'zeus_sources' : 'default_sources'
        ).then((data) => data.sources),

      update_additional_sources: (sources: unknown[]) =>
        fetchWithAuth<{ config: unknown }>('additional_sources', {
          method: 'POST',
          data: { sources },
        }).then((resp) => resp.config),

      fetch_transaction_value_overrides: () =>
        fetchWithAuth('accounting/transaction_value_overrides'),

      update_transaction_value_overrides: (overrides: unknown) =>
        fetchWithAuth('accounting/transaction_value_overrides', {
          method: 'POST',
          data: { overrides },
        }),

      delete_transaction_value_overrides: (transaction_value_override_ids: unknown) =>
        fetchWithAuth('accounting/transaction_value_overrides', {
          method: 'DELETE',
          data: { transaction_value_override_ids },
        }),

      fetch_transaction_categories: (params: FetchTransactionCategoryParams) =>
        fetchWithAuth('transaction_category', {
          params,
          responseSchema: fetchTransactionCategoriesResultSchema,
        }),

      create_transaction_category: (data: CreateTransactionCategoryParams) =>
        fetchWithAuth('transaction_category', {
          method: 'POST',
          data,
        }),

      fetch_custom_erp_mapping_enabled_setting: () =>
        fetchWithAuth<{ result: unknown }>('custom_erp_mapping_enabled').then(
          (resp) => resp.result
        ),

      update_custom_erp_mapping_enabled_setting: (setting: boolean) =>
        fetchWithAuth('custom_erp_mapping_enabled', {
          method: 'POST',
          data: { org_setting: 'custom_erp_mapping_enabled', value: setting },
        }),

      get_erp_accounts_csv: () =>
        fetchWithAuth('ingestion/erp_accounts_csv', {
          method: 'GET',
          responseSchema: erpAccountsCsvResponseSchema,
        }),

      get_erp_accounts_csv_by_name: (name: string) =>
        fetchWithAuth('ingestion/erp_accounts_csv', {
          method: 'GET',
          params: { name },
          responseSchema: erpAccountsCsvByNameResponseSchema,
        }),

      upload_erp_accounts_csv: (formdata: FormData) =>
        fetchWithAuth('ingestion/erp_accounts_csv', {
          method: 'POST',
          body: formdata,
          responseSchema: customErpCsvUploadResponseSchema,
        }),

      fetch_earliest_transaction_date: (): Promise<FetchEarliestTransactionDateResponse> =>
        fetchWithAuth<FetchEarliestTransactionDateResponse>(
          'transaction/earliest_transaction_date'
        ),

      fetch_report: (
        report_type: NonSpecificReportType,
        params: FetchPaginatedReportRequestParams
      ) =>
        fetchWithAuth<FetchPaginatedReportResponse>(`reports/${report_type}`, {
          params,
        }),

      fetch_asset_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.Asset}`, {
          params,
          responseSchema: assetReportSchema,
        }),

      fetch_gainloss_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.GainLoss}`, {
          params,
          responseSchema: gainLossReportSchema,
        }),

      fetch_rolling_gainloss_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.RollingGainLoss}`, {
          params,
          responseSchema: rollingGainLossReportSchema,
        }),

      fetch_impairment_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.ImpairmentSummary}`, {
          params,
          responseSchema: impairmentSummaryReportSchema,
        }),

      fetch_impairment_lot_level_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.ImpairmentLotLevel}`, {
          params,
          responseSchema: impairmentLotLevelReportSchema,
        }),

      fetch_impairment_lot_ending_inventory_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.ImpairmentLotEndingInventory}`, {
          params,
          responseSchema: impairmentLotEndingInventorySchema,
        }),

      fetch_impairment_disposition_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.ImpairmentDisposition}`, {
          params,
          responseSchema: impairmentDispositionSchema,
        }),

      fetch_qa_impairment_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaImpairmentSummary}`, {
          params,
          responseSchema: qaImpairmentSummarySchema,
        }),

      fetch_significant_holdings_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.SignificantHoldings}`, {
          params,
          responseSchema: significantHoldingsReportSchema,
        }),

      fetch_reconciliation_roll_forward_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.ReconciliationRollForward}`, {
          params,
          responseSchema: reconciliationRollForwardReportSchema,
        }),

      fetch_asc_350_60_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.Asc35060}`, {
          params,
          responseSchema: asc35060ReportSchema,
        }),

      fetch_prices_daily_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.PricesDailySummary}`, {
          params,
          responseSchema: pricesDailySummarySchema,
        }),

      fetch_qa_roll_forward_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaRollForward}`, {
          params,
          responseSchema: qaRollForwardSchema,
        }),

      fetch_roll_forward_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.RollForward}`, {
          params,
          responseSchema: legacyRollForwardSchema,
        }),

      fetch_qa_asset_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaAsset}`, {
          params,
          responseSchema: qaAssetDataSchema,
        }),

      fetch_qa_balance_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaBalance}`, {
          params,
          responseSchema: qaBalanceSchema,
        }),

      fetch_qa_significant_holdings_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaSignificantHoldings}`, {
          params,
          responseSchema: qaSignificantHoldingsSchema,
        }),

      fetch_qa_global_entity_holdings_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaGlobalEntityHoldings}`, {
          params,
          responseSchema: qaGlobalEntityHoldingsSchema,
        }),

      fetch_qa_global_asset_holdings_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaGlobalAssetHoldings}`, {
          params,
          responseSchema: qaGlobalAssetHoldingsSchema,
        }),

      fetch_significant_holdings_detailed_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.SignificantHoldingsDetailed}`, {
          params,
          responseSchema: significantHoldingsDetailedSchema,
        }),

      fetch_qa_fair_market_value_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaFairMarketValueSummary}`, {
          params,
          responseSchema: qaFairMarketValueSummarySchema,
        }),

      fetch_fair_market_value_lot_dispositions_report: (
        params: FetchPaginatedReportRequestParams
      ) =>
        fetchWithAuth(`reports/${ReportType.FairMarketValueLotDispositions}`, {
          params,
          responseSchema: fairMarketValueLotDispositionsSchema,
        }),

      fetch_fair_market_value_lot_ending_inventory_report: (
        params: FetchPaginatedReportRequestParams
      ) =>
        fetchWithAuth(`reports/${ReportType.FairMarketValueLotEndingInventory}`, {
          params,
          responseSchema: fairMarketValueLotEndingInventorySchema,
        }),

      fetch_balance_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.Balance}`, {
          params,
          responseSchema: balanceSchema,
        }),

      fetch_nft_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.Nft}`, {
          params,
          responseSchema: nftSchema,
        }),

      fetch_fair_market_value_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.FairMarketValueSummary}`, {
          params,
          responseSchema: fairMarketValueSummarySchema,
        }),

      fetch_fair_market_value_lot_level_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.FairMarketValueLotLevel}`, {
          params,
          responseSchema: fairMarketValueLotLevelSchema,
        }),

      fetch_journal_entry_detailed_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.JournalEntryDetailed}`, {
          params,
          responseSchema: journalEntryDetailedSchema,
        }),

      fetch_qa_journal_entry_detailed_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.QaJournalEntryDetailed}`, {
          params,
          responseSchema: qaJournalEntryDetailedSchema,
        }),

      fetch_journal_entry_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.JournalEntrySummary}`, {
          params,
          responseSchema: journalEntrySummarySchema,
        }),

      fetch_gainloss_summary_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.GainlossSummary}`, {
          params,
          responseSchema: gainlossSummarySchema,
        }),

      fetch_transaction_edit_history_report: (params: FetchPaginatedReportRequestParams) =>
        fetchWithAuth(`reports/${ReportType.TransactionEditHistory}`, {
          params,
          responseSchema: transactionEditHistorySchema,
        }),

      fetch_favorite_reports: () =>
        fetchWithAuth('reports/favorites', {
          method: 'GET',
          responseSchema: favoriteReportsResponseSchema,
        }),

      update_favorite_report: (reportType: string, isFavorite: boolean) => {
        // The backend API expects 'POST' to add a favorite and 'PUT' to remove one.
        // This is why the method selection logic defaults to 'POST' for adding and 'PUT' for removing.
        const method = isFavorite ? 'POST' : 'PUT'

        return fetchWithAuth(`reports/favorites/${reportType}`, {
          method,
          data: { is_favorite: isFavorite },
          responseSchema: updateFavoriteReportResponseSchema,
        })
      },

      fetch_dashboard_asset_by_date: (params?: Partial<DashboardParamsType>) =>
        fetchWithAuth<FetchAssetByDateResponse>('dashboard/asset_by_date', {
          params,
        }),

      fetch_dashboard_current_assets: (params?: Partial<DashboardFilterParamType>) =>
        fetchWithAuth<FetchCurrentAssetsApiResponse>('dashboard/current_assets', {
          params,
        }),

      fetch_dashboard_summary: (params: DashboardFilterParamType) =>
        fetchWithAuth<FetchDashboardSummaryResponse>('dashboard/summary', {
          params,
        }),

      fetch_dashboard_main_transactions_count: (params: DashboardFilterParamType) =>
        fetchWithAuth<FetchMainTransactionCountResponse>('dashboard/main_transactions_count', {
          method: 'POST',
          data: params,
        }),

      fetch_transaction_main_transaction_count: (params: TransactionFilterParams) =>
        fetchWithAuth('transaction/main_transaction_count', {
          method: 'POST',
          data: params,
          responseSchema: transactionCountSchema,
        }),

      fetch_report_download_url: (report_type: ReportType, params: FetchReportDownloadUrlParams) =>
        fetchWithAuth<FetchReportDownloadUrlResponse>(`export/${report_type}`, {
          params,
        }),

      fetch_impaired_assets: () =>
        fetchWithAuth('impaired_assets', {
          responseSchema: impairedAssetsSchema,
        }),

      update_impaired_assets: (impairedaAssets: string[]) =>
        fetchWithAuth('impaired_assets', {
          method: 'POST',
          data: { assets: impairedaAssets },
          responseSchema: updateImpairedAssetsSchema,
        }),

      fetch_transaction_download_url: (params: TransactionFilterParams) =>
        fetchWithAuth('transaction/export', {
          params,
          responseSchema: transactionExportSchema,
        }),

      fetch_filter_options: (params: FetchFilterOptionsParams) =>
        fetchWithAuth<FetchFilterOptionsResponse>('filter_options', {
          params,
        }),

      fetch_bulk_edit_status: (params: BulkEditStatusParams) =>
        fetchWithAuth('transaction/bulk_edit_status', {
          params,
          responseSchema: bulkEditStatusSchema,
        }),

      fetch_org_settings: () =>
        fetchWithAuth('settings', {
          responseSchema: fetchOrgSettingsResponseSchema,
        }),

      update_org_settings: (org_setting: string, value: unknown): Promise<unknown> =>
        fetchWithAuth('settings', {
          method: 'POST',
          data: { org_setting, value },
        }),

      fetch_org_metadata: () =>
        fetchWithAuth('org/metadata', {
          responseSchema: fetchOrgMetadataResponseSchema,
        }),

      update_org_metadata: (metadata: OrgMetadata) =>
        fetchWithAuth('org/metadata', {
          method: 'PUT',
          data: metadata,
        }),

      fetch_org_entities: () => fetchWithAuth(`legal_entity/entity`),

      fetch_user_org_entities: () =>
        fetchWithAuth<FetchUserOrgEntitiesResponse>(`legal_entity/entity/user`),

      fetch_org_accounting_config: (params: { book_type?: BookType }) =>
        fetchWithAuth('accounting/config', {
          params,
          responseSchema: fetchAccountingConfigResponseSchema,
        }),

      update_org_accounting_config: (key: string, value: string | boolean, book_type?: BookType) =>
        fetchWithAuth('accounting/config', {
          method: 'POST',
          data: { key, value, book_type },
          responseSchema: fetchAccountingConfigResponseSchema,
        }),

      fetch_org_pricing_setting: () =>
        fetchWithAuth('pricing/setting', {
          responseSchema: fetchPricingSettingsResponseSchema,
        }),

      update_org_pricing_setting: (
        price_setting: UpdatePricingSettingRequest | UpdatePricingMethodRequest
      ) =>
        fetchWithAuth('pricing/setting', {
          method: 'POST',
          data: { price_setting },
        }),

      fetch_org_pricing_overrides: () =>
        fetchWithAuth('pricing/asset_providers', {
          responseSchema: fetchPricingOverridesResponseSchema,
        }),

      upsert_org_pricing_override: (asset: string, provider: PricingMarketProvider) =>
        fetchWithAuth('pricing/asset_providers', {
          responseSchema: upsertPricingOverrideResponseSchema,
          method: 'POST',
          data: {
            asset,
            provider,
          },
        }),

      delete_org_pricing_override: (asset: string) =>
        fetchWithAuth('pricing/asset_providers', {
          responseSchema: deletePricingOverrideResponseSchema,
          method: 'DELETE',
          data: {
            asset,
          },
        }),

      refresh_prices: (request: RefreshPricesRequest) =>
        fetchWithAuth('refresh-requests', {
          responseSchema: refreshPricesResponseSchema,
          method: 'POST',
          data: request,
        }),

      trigger_gc_dag: (context: AirflowDagContext) =>
        fetchWithAuth('tasks/gc_dag', {
          method: 'POST',
          data: { context },
        }),

      fetch_gc_dag_runs: () =>
        fetchWithAuth('tasks/gc_dag', {
          responseSchema: airflowDagRunsResponseSchema,
          method: 'GET',
          timeout: 30_000,
        }),

      fetch_gc_dag_next_run: () =>
        fetchWithAuth('tasks/gc_dag/next_run', {
          responseSchema: airflowDagNextRunSchema,
          method: 'GET',
          timeout: 30_000,
        }),

      fetch_gc_dag_run: (runId: string) =>
        fetchWithAuth(`tasks/gc_dag/${runId}`, {
          responseSchema: airflowDagRunSchema,
          method: 'GET',
        }),

      fetch_gc_dag_run_tasks: (runId: string) =>
        fetchWithAuth(`tasks/gc_dag/${runId}/tasks`, {
          responseSchema: z.object({ tasks: z.array(airflowTaskInstanceSchema) }),
          method: 'GET',
          timeout: 30_000,
        }),

      stop_gc_dag_run: (runId: string) =>
        fetchWithAuth(`tasks/gc_dag/${runId}`, {
          method: 'DELETE',
        }),

      fetch_gc_dag_url: () => fetchWithAuth<{ url: string }>('tasks/gc_dag_url'),

      fetch_pending_changes: () =>
        fetchWithAuth('tasks/pending_changes', {
          responseSchema: pendingChangesResponseSchema,
        }),

      fetch_accounting_sync_statuses: (params: FetchAccountingSyncStatusParams) =>
        fetchWithAuth<FetchAccountingSyncStatusResponse>('transactions/accounting_sync_statuses', {
          method: 'POST',
          data: params,
        }),

      create_legal_entity: (entity_name: string) =>
        fetchWithAuth('legal_entity/create', {
          method: 'POST',
          data: { entity_name },
        }),

      delete_legal_entity: (entity_name: string) =>
        fetchWithAuth('legal_entity/entity', {
          method: 'DELETE',
          data: { entity_name },
        }),

      fetch_pma_exchanges: () =>
        fetchWithAuth('principal_market_analysis/exchanges', {
          responseSchema: fetchPmaExchangesResponseSchema,
        }),

      fetch_pma_currencies: () =>
        fetchWithAuth('principal_market_analysis/currencies', {
          responseSchema: fetchPmaCurrenciesResponseSchema,
        }),

      fetch_pma_analyses: () =>
        fetchWithAuth('principal_market_analysis/analyses', {
          responseSchema: fetchPmaAnalysesResponseSchema,
        }),

      create_pma_analysis: (newAnalysis: CreatePmaAnalysisRequest) =>
        fetchWithAuth('principal_market_analysis/analysis', {
          method: 'POST',
          data: newAnalysis,
          responseSchema: pmaAnalysisSchema,
        }),

      update_pma_analysis: (id: string, analysis: CreatePmaAnalysisRequest) =>
        fetchWithAuth(`principal_market_analysis/analysis/${id}`, {
          method: 'PUT',
          data: analysis,
          responseSchema: pmaAnalysisSchema,
        }),

      delete_pma_analyses: (ids: string[]) =>
        fetchWithAuth('principal_market_analysis/analyses', {
          method: 'DELETE',
          data: ids,
        }),

      run_pma_analysis: (id: string) =>
        fetchWithAuth(`principal_market_analysis/run_analysis/${id}`, {
          method: 'POST',
          responseSchema: runPmaAnalysisResponseSchema,
        }),

      fetch_audit_log_export: (start_date: string, end_date: string) =>
        fetchWithAuth('audit_log', {
          method: 'GET',
          params: { start_date, end_date },
          responseSchema: z.object({ url: z.string() }),
        }),

      voucher_users: () =>
        fetchWithAuth('voucher_users', {
          responseSchema: voucherUsersResponseSchema,
          method: 'GET',
        }),

      voucher_list: () =>
        fetchWithAuth('vouchers', {
          responseSchema: voucherListResponseSchema,
          method: 'GET',
        }),

      voucher_get: (voucher_id: string) =>
        fetchWithAuth(`vouchers/${voucher_id}`, {
          responseSchema: voucherGetResponseSchema,
          method: 'GET',
        }),

      voucher_create: (data: VoucherCreateRequest) =>
        fetchWithAuth('vouchers', {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_delete: (voucher_id: string, data: VoucherDeleteRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}`, {
          responseSchema: voucherActionResponseSchema,
          method: 'DELETE',
          data: data,
        }),

      voucher_cancel: (voucher_id: string, data: VoucherCancelRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/cancel`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_edit_draft: (voucher_id: string, data: VoucherEditDraftRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/edit-draft`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_edit_review: (voucher_id: string, data: VoucherEditReviewRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/edit-in-review`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_add_comment: (voucher_id: string, data: VoucherAddCommentRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/add-comment`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_add_transactions: (voucher_id: string, data: VoucherAddTransactionsRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/add-transactions`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_edit_transaction: (
        voucher_id: string,
        group_id: string,
        data: VoucherEditSingleTransactionRequest
      ) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/edit-transaction/${group_id}`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_remove_transactions: (voucher_id: string, data: VoucherRemoveTransactionsRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/remove-transactions`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_submit_draft: (voucher_id: string, data: VoucherSubmitDraftRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/submit-draft`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_submit_audit: (voucher_id: string, data: VoucherSubmitAuditRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/submit-audit`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_submit_approval: (voucher_id: string, data: VoucherSubmitApprovalRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/submit-approval`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_submit_certification: (voucher_id: string, data: VoucherSubmitCertificationRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/submit-certification`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_rework: (voucher_id: string, data: VoucherReworkRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/rework`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_sync: (voucher_id: string, data: VoucherSyncRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/sync-voucher`, {
          responseSchema: voucherActionResponseSchema,
          method: 'POST',
          data: data,
        }),

      voucher_export: (voucher_id: string, data: VoucherExportRequest) =>
        fetchWithAuth(`vouchers/${voucher_id}/action/export-voucher`, {
          responseSchema: voucherExportResponseSchema,
          method: 'POST',
          data: data,
        }),

      upload_price_override: (formdata: FormData) =>
        fetchWithAuth('prices/override-upload', {
          method: 'POST',
          responseSchema: priceOverrideUploadResponseSchema,
          body: formdata,
        }),

      fetch_price_override_uploads: () =>
        fetchWithAuth('prices/override-uploads', {
          responseSchema: priceOverrideUploadsResponseSchema,
        }),
    }
  }, [fetchWithAuth])

  return api
}

export type Api = ReturnType<typeof useApiInternal>

export const ApiContext = createContext<Api | undefined>(undefined)

export const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  const api = useApiInternal()

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
}

/**
 * Hook to access all authenticated API calls. Must be nested inside of an ApiContextProvider,
 * or an error will throw when attempting to access any API endpoints.
 */
function useApi() {
  const api = useContext(ApiContext)

  if (!api) {
    throw new Error('Authenticated API used outside of ApiContextProvider')
  }

  return api
}

export default useApi
